import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { CollisionPriority } from "@dnd-kit/abstract";
import { DragDropProvider, useDroppable } from "@dnd-kit/react";
import { move } from "@dnd-kit/helpers";
import "./dashboard.css";
import Api from "../../Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Service from "./Service";
import Details from "./Details";
import classNames from "classnames";
import {
  DashboardState,
  InstallationsMap,
  TechniciansMap,
  User,
} from "../../types";

const InstallationsDashboard = () => {
  const {
    unallocated,
    pending,
    allocated,
    techniciansMap,
    installationsMap,
    loading,
    mutate,
    save,
    sendTo,
    markComplete,
    load,
  } = useData();

  const [showDetails, setShowDetails] = useState<string | null>(null);

  return (
    <DragDropProvider onDragOver={mutate} onDragEnd={save}>
      <div className="flex flex-col md:flex-row gap-4 h-full w-full p-4">
        <QueueBase
          items={unallocated}
          installationsMap={installationsMap}
          onSelect={setShowDetails}
          sendTo={sendTo("base")}
        />

        <div className="flex-1 flex flex-col gap-4 overflow-scroll no-scrollbar">
          {Object.entries(allocated).map(([uuid, queue]) => (
            <Worker
              key={uuid}
              uuid={uuid}
              queue={queue}
              technician={techniciansMap[uuid]}
              installationsMap={installationsMap}
              onSelect={setShowDetails}
              sendTo={sendTo(uuid)}
            />
          ))}

          <Worker
            key={"pending"}
            uuid={"pending"}
            queue={pending}
            technician={null}
            installationsMap={installationsMap}
            onSelect={setShowDetails}
            sendTo={sendTo("pending")}
            alt={true}
          />
        </div>

        {showDetails && (
          <Details
            onClose={() => setShowDetails(null)}
            installation={installationsMap[showDetails].installation}
            clientName={installationsMap[showDetails].clientName}
            sellerName={installationsMap[showDetails].sellerName}
            markComplete={markComplete(showDetails)}
            onAddNote={load}
          />
        )}
      </div>

      {loading && <Loading />}
    </DragDropProvider>
  );
};

export default InstallationsDashboard;

const QueueBase = ({
  items,
  installationsMap,
  onSelect,
  sendTo,
}: {
  items: string[];
  installationsMap: InstallationsMap;
  onSelect: (id: string) => void;
  sendTo: (uuid: string) => (beginning: boolean) => () => void;
}) => {
  const { ref } = useDroppable({
    id: "base",
    type: "column",
    accept: "item",
    collisionPriority: CollisionPriority.Low,
  });

  return (
    <div className="min-w-[248px] bg-gray-200 rounded-lg overflow-hidden">
      <div className="text-xs font-semibold p-2 text-center">
        {items.length} {items.length === 1 ? "instalação" : "instalações"}
      </div>

      <div
        ref={ref}
        className="flex md:flex-col gap-2 max-h-full px-2 pb-2 md:pb-32 overflow-y-scroll"
      >
        {items.map((id) => (
          <Service
            key={id}
            installation={installationsMap[id].installation}
            clientName={installationsMap[id].clientName}
            sellerName={installationsMap[id].sellerName}
            onClick={() => onSelect(id)}
            sendTo={sendTo(id)}
          />
        ))}
      </div>
    </div>
  );
};

const Worker = ({
  uuid,
  queue,
  technician,
  installationsMap,
  onSelect,
  sendTo,
  alt = false,
}: {
  uuid: string;
  queue: string[];
  technician: User | null;
  installationsMap: InstallationsMap;
  onSelect: (id: string) => void;
  sendTo: (uuid: string) => (beginning: boolean) => () => void;
  alt?: boolean;
}) => {
  return (
    <div>
      <div
        className={classNames(
          `text-xs border border-gray-200 border-b-0 rounded-t-lg py-2 px-4 font-bold flex gap-2`,
          {
            "bg-yellow-100 text-yellow-700 border-yellow-600": alt,
          }
        )}
      >
        <div>
          {technician
            ? `${technician.firstName} ${technician.lastName}`
            : "Pendente"}
        </div>

        <div
          className={classNames("font-bold rounded-full px-1", {
            "bg-gray-900 text-white": !alt,
            "bg-yellow-600 text-yellow-100": alt,
          })}
        >
          {queue.length}
        </div>
      </div>

      <Queue
        id={uuid}
        items={queue}
        installationsMap={installationsMap}
        onSelect={onSelect}
        sendTo={sendTo}
        alt={alt}
      />
    </div>
  );
};

const Queue = ({
  id,
  items,
  installationsMap,
  onSelect,
  sendTo,
  alt = false,
}: {
  id: string;
  items: string[];
  installationsMap: InstallationsMap;
  onSelect: (id: string) => void;
  sendTo: (uuid: string) => (beginning: boolean) => () => void;
  alt: boolean;
}) => {
  const { ref } = useDroppable({
    id,
    type: "row",
    accept: "item",
    collisionPriority: CollisionPriority.Low,
  });

  return (
    <div
      ref={ref}
      className={classNames(
        "flex w-full p-2 gap-2 min-h-[143px] overflow-scroll rounded-b-lg bg-gray-200 border",
        {
          "bg-yellow-50 border-yellow-600": alt,
        }
      )}
    >
      {items.map((id) => (
        <Service
          key={id}
          id={id}
          installation={installationsMap[id].installation}
          clientName={installationsMap[id].clientName}
          sellerName={installationsMap[id].sellerName}
          onClick={() => onSelect(id)}
          sendTo={sendTo(id)}
          vertical={false}
        />
      ))}
    </div>
  );
};

const useData = () => {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<DashboardState>({ base: [], pending: [] });
  const [techniciansMap, setTechnicianMap] = useState<TechniciansMap>({});
  const [installationsMap, setInstallationsMap] = useState<InstallationsMap>(
    {}
  );
  const [shouldSave, setShouldSave] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Api.installationsGet()
      .then(({ state, techniciansMap, installationsMap }) => [
        setState(state),
        setTechnicianMap(techniciansMap),
        setInstallationsMap(installationsMap),
      ])
      .then(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(() => {
    setLoading(true);
    Api.installationsUpdate(state).then(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    if (shouldSave) {
      save();
    }
  }, [state, shouldSave, save]);

  const mutate = (event: any) => {
    setShouldSave(false);
    setState(move(state, event));
  };

  const sendTo =
    (key: string) => (installation: string) => (beginning: boolean) => () => {
      setShouldSave(true);
      setState((state) => ({
        ...state,
        [key]: beginning
          ? _.concat([installation], _.without(state[key], installation))
          : _.concat(_.without(state[key], installation), [installation]),
      }));
    };

  const markComplete = (uuid: string) => () => {
    Api.installationsMarkComplete(uuid).then(load);
  };

  return {
    loading,
    unallocated: state.base,
    pending: state.pending,
    allocated: _.omit(state, ["base", "pending"]),
    techniciansMap,
    installationsMap,
    mutate,
    sendTo,
    save,
    markComplete,
    load,
  };
};

const Loading = () => (
  <div className="absolute top-0 left-0 w-full h-full bg-black/50">
    <div className="flex justify-center items-center h-full">
      <div className="bg-black/50 w-20 h-20 flex items-center justify-center rounded-3xl text-3xl text-gray-100">
        <FontAwesomeIcon icon="circle-notch" spin={true} />
      </div>
    </div>
  </div>
);
